import {
  Badge,
  Button,
  Checkbox,
  Flex,
  Group,
  Image,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconUpload } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useSearchResources } from '@medplum/react-hooks';
import { ORG_TYPE_PAYER } from '@unify/fhir-model/constants';
import { calendarDateFromPickerValue, dateInputValueToDateOnly, parseDateOnlyLocal } from '@/lib/dateHelpers';
import { useResolvedImageUrl } from '@/hooks/useResolvedImageUrl';
import type { InsuranceCardDocument } from '@/lib/billing/insuranceCards';
import { formatDate } from './helpers';
import type { InsuranceEntry, InsurancePriority } from './types';
import { maskId, priorityLabel } from './helpers';

const PRIORITY_BADGE_COLOR: Record<InsurancePriority, string> = {
  primary: 'gold',
  secondary: 'blue',
  tertiary: 'gray',
};

export interface InsurancePlanModalProps {
  opened: boolean;
  mode: 'add' | 'edit';
  draft: InsuranceEntry | null;
  insuranceTypeOptions?: { value: string; label: string }[];
  /** Masked member ID hint when editing (portal/staff). */
  memberIdHint?: string;
  /** When true, shows front/back card photo upload rows (add and edit). */
  showCardPhotos?: boolean;
  cardPhotos?: {
    front?: InsuranceCardDocument;
    back?: InsuranceCardDocument;
  };
  saving?: boolean;
  onClose: () => void;
  onDraftChange: (draft: InsuranceEntry) => void;
  onSubmit: (draft: InsuranceEntry, cardFiles: { front?: File; back?: File }) => void | Promise<void>;
}

function InsuranceCardUploadRow({
  side,
  doc,
  pendingFile,
  onSelect,
}: {
  side: 'front' | 'back';
  doc?: InsuranceCardDocument;
  pendingFile?: File;
  onSelect: (file: File) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const resolvedDocUrl = useResolvedImageUrl(doc?.url);

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    onSelect(file);
  };

  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(undefined);
    return undefined;
  }, [pendingFile]);

  const displayUrl = pendingFile ? previewUrl : resolvedDocUrl;

  return (
    <Stack gap="xs" style={{ flex: 1 }}>
      <Text size="sm" fw={600} tt="capitalize">{side} of card</Text>
      {displayUrl ? (
        <Image src={displayUrl} alt={`Insurance card ${side}`} mah={120} fit="contain" radius="sm" />
      ) : (
        <Text size="sm" c="dimmed">No {side} image on file.</Text>
      )}
      {doc?.uploadedAt && !pendingFile && (
        <Text size="xs" c="dimmed">Uploaded {formatDate(doc.uploadedAt)}</Text>
      )}
      {pendingFile && (
        <Text size="xs" c="dimmed">{pendingFile.name} (not saved yet)</Text>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <Button
        size="xs"
        variant="light"
        leftSection={<IconUpload size={14} />}
        onClick={() => inputRef.current?.click()}
      >
        {doc || pendingFile ? 'Replace' : 'Upload'} {side}
      </Button>
    </Stack>
  );
}

export function InsurancePlanModal({
  opened,
  mode,
  draft,
  insuranceTypeOptions,
  memberIdHint,
  showCardPhotos = false,
  cardPhotos,
  saving,
  onClose,
  onDraftChange,
  onSubmit,
}: InsurancePlanModalProps): JSX.Element | null {
  const [pendingFront, setPendingFront] = useState<File | undefined>();
  const [pendingBack, setPendingBack] = useState<File | undefined>();
  const [payerOrgs] = useSearchResources('Organization', {
    type: ORG_TYPE_PAYER,
    _count: '200',
    _sort: 'name',
  });
  const payerOptions = useMemo(
    () =>
      (payerOrgs ?? [])
        .filter((o) => o.id && o.name)
        .map((o) => ({ value: o.id as string, label: o.name as string })),
    [payerOrgs],
  );

  useEffect(() => {
    if (!opened) {
      setPendingFront(undefined);
      setPendingBack(undefined);
    }
  }, [opened, draft?.id]);

  if (!draft) return null;

  const priority = draft.priority;
  const isAdd = mode === 'add';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(draft, { front: pendingFront, back: pendingBack });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isAdd ? 'Add insurance plan' : 'Update insurance'}
      centered
      size={showCardPhotos ? 'lg' : 'md'}
    >
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <Group gap="sm" align="flex-start">
            <Badge size="lg" variant="light" color={PRIORITY_BADGE_COLOR[priority]}>
              {priorityLabel(priority)}
            </Badge>
            <Text size="sm" c="dimmed">
              {isAdd
                ? 'Adding a new plan at this priority level.'
                : 'Updating this plan on file.'}
            </Text>
          </Group>

          <Select
            label="Plan priority"
            description="Each client can have one primary, one secondary, and one tertiary plan."
            value={draft.priority}
            onChange={(v) => {
              if (v === 'primary' || v === 'secondary' || v === 'tertiary') {
                onDraftChange({ ...draft, priority: v });
              }
            }}
            data={[
              { value: 'primary', label: 'Primary' },
              { value: 'secondary', label: 'Secondary' },
              { value: 'tertiary', label: 'Tertiary' },
            ]}
          />

          <Select
            label="Insurance company"
            placeholder="Select payer"
            value={draft.payerOrgId ?? null}
            onChange={(v) => {
              const selected = payerOptions.find((o) => o.value === v);
              onDraftChange({
                ...draft,
                payerOrgId: v ?? undefined,
                provider: selected?.label ?? null,
              });
            }}
            searchable
            data={payerOptions}
            required
          />

          {insuranceTypeOptions && insuranceTypeOptions.length > 0 && (
            <Select
              label="Insurance type"
              placeholder="Select type"
              value={draft.insuranceType ?? null}
              onChange={(v) => onDraftChange({ ...draft, insuranceType: v ?? undefined })}
              data={insuranceTypeOptions}
            />
          )}

          <TextInput
            label="Plan name"
            placeholder="Optional"
            value={draft.planName ?? ''}
            onChange={(e) => onDraftChange({ ...draft, planName: e.currentTarget.value })}
          />

          <Flex gap="md">
            <TextInput
              style={{ flex: 1 }}
              label="Member ID"
              placeholder="Enter member ID"
              description={memberIdHint ? `Current ID on file: ${memberIdHint}` : undefined}
              value={draft.memberId}
              onChange={(e) => onDraftChange({ ...draft, memberId: e.currentTarget.value })}
              required
            />
            <TextInput
              style={{ flex: 1 }}
              label="Group number"
              placeholder="Optional"
              value={draft.groupId}
              onChange={(e) => onDraftChange({ ...draft, groupId: e.currentTarget.value })}
            />
          </Flex>

          <Checkbox
            checked={draft.subscriberIsClient}
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              onDraftChange({
                ...draft,
                subscriberIsClient: checked,
                subscriberFirstName: checked ? '' : draft.subscriberFirstName,
                subscriberLastName: checked ? '' : draft.subscriberLastName,
                subscriberDob: checked ? null : draft.subscriberDob,
                subscriberRelationship: checked ? null : draft.subscriberRelationship,
              });
            }}
            label="Client is the subscriber on this plan"
          />

          {!draft.subscriberIsClient && (
            <Stack gap="md">
              <Title order={6}>Subscriber details</Title>
              <Flex gap="md">
                <TextInput
                  style={{ flex: 1 }}
                  label="First name"
                  value={draft.subscriberFirstName}
                  onChange={(e) => onDraftChange({ ...draft, subscriberFirstName: e.currentTarget.value })}
                />
                <TextInput
                  style={{ flex: 1 }}
                  label="Last name"
                  value={draft.subscriberLastName}
                  onChange={(e) => onDraftChange({ ...draft, subscriberLastName: e.currentTarget.value })}
                />
              </Flex>
              <Flex gap="md">
                <DateInput
                  style={{ flex: 1 }}
                  label="Date of birth"
                  value={draft.subscriberDob}
                  onChange={(v) => onDraftChange({ ...draft, subscriberDob: calendarDateFromPickerValue(v) })}
                />
                <Select
                  style={{ flex: 1 }}
                  label="Relationship to subscriber"
                  value={draft.subscriberRelationship}
                  onChange={(v) => {
                    if (v === 'spouse' || v === 'child' || v === 'other' || v === null) {
                      onDraftChange({ ...draft, subscriberRelationship: v });
                    }
                  }}
                  data={[
                    { value: 'spouse', label: 'Spouse' },
                    { value: 'child', label: 'Parent / guardian is subscriber' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </Flex>
            </Stack>
          )}

          <Flex gap="md">
            <DateInput
              style={{ flex: 1 }}
              label="Effective start"
              value={draft.periodStart ? parseDateOnlyLocal(draft.periodStart) : null}
              onChange={(v) =>
                onDraftChange({
                  ...draft,
                  periodStart: v ? dateInputValueToDateOnly(v as Date | string | null) : undefined,
                })
              }
            />
            <DateInput
              style={{ flex: 1 }}
              label="Effective end"
              value={draft.periodEnd ? parseDateOnlyLocal(draft.periodEnd) : null}
              onChange={(v) =>
                onDraftChange({
                  ...draft,
                  periodEnd: v ? dateInputValueToDateOnly(v as Date | string | null) : undefined,
                })
              }
            />
          </Flex>

          {showCardPhotos && (
            <Stack gap="xs">
              <Title order={6}>Insurance card photos</Title>
              <Text size="sm" c="dimmed">
                Upload photos for this plan. Replacing a card keeps the prior file in history.
              </Text>
              <Group align="flex-start" grow>
                <InsuranceCardUploadRow
                  side="front"
                  doc={cardPhotos?.front}
                  pendingFile={pendingFront}
                  onSelect={(file) => setPendingFront(file)}
                />
                <InsuranceCardUploadRow
                  side="back"
                  doc={cardPhotos?.back}
                  pendingFile={pendingBack}
                  onSelect={(file) => setPendingBack(file)}
                />
              </Group>
            </Stack>
          )}

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="default" onClick={onClose} type="button" disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" color="gold" loading={saving}>
              {isAdd ? 'Add insurance' : 'Save insurance'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function emptyInsuranceDraft(priority: InsurancePriority): InsuranceEntry {
  return {
    id: `ins-${Math.random().toString(16).slice(2)}`,
    priority,
    provider: null,
    memberId: '',
    groupId: '',
    subscriberIsClient: true,
    subscriberFirstName: '',
    subscriberLastName: '',
    subscriberDob: null,
    subscriberRelationship: null,
    verificationStatus: 'pending',
  };
}

export function maskedMemberIdHint(memberId: string): string | undefined {
  if (!memberId.trim()) return undefined;
  return maskId(memberId);
}
